import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../../config/supabase';
import { Search, Plus, BookOpen, Filter, Info, User } from '../../lib/lucide-proxy';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Modal from '../../components/ui/Modal';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const BeerEncyclopedia = () => {
  const [beers, setBeers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal State
  const [selectedBeer, setSelectedBeer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchBeers();
  }, []);

  const fetchBeers = async () => {
    try {
      // Include author name in fetch
      const { data, error } = await supabase
        .from('beer_encyclopedia')
        .select('*, author:users(full_name)')
        .eq('is_approved', true)
        .order('name');

      if (error) throw error;
      setBeers(data || []);
    } catch (error) {
      console.error('Error fetching encyclopedia:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBeerClick = (beer) => {
    setSelectedBeer(beer);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedBeer(null), 300);
  };

  const filteredBeers = beers.filter(beer => 
    beer.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    beer.style.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <title>Enciclopédia da Cerveja - Cerveja Delivery</title>
      </Helmet>

      <div className="bg-cream-300 min-h-screen pb-20">
        {/* Header Section */}
        <div className="bg-dark-900 py-16 text-center px-4 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          <div className="relative z-10 max-w-3xl mx-auto">
            <BookOpen className="w-12 h-12 text-primary-500 mx-auto mb-4" />
            <h1 className="font-display font-bold text-4xl md:text-5xl text-white mb-4">
              Enciclopédia Cervejeira
            </h1>
            <p className="text-gray-300 text-lg mb-8">
              O bulário definitivo da cerveja artesanal. Descubra estilos, histórias e harmonizações.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input 
                  type="text" 
                  placeholder="Buscar estilo ou cerveja..." 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:outline-none backdrop-blur-sm transition-all focus:bg-white/20" 
                />
              </div>
              <Button variant="primary" onClick={() => navigate('/cervejas/nova')} className="whitespace-nowrap shadow-lg shadow-primary-500/20">
                <Plus className="w-4 h-4 mr-2" /> Contribuir
              </Button>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {loading ? (
            <div className="flex justify-center"><LoadingSpinner size="lg" /></div>
          ) : filteredBeers.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-100">
              <Info className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-dark-500 mb-4">Nenhum registro encontrado para sua busca.</p>
              <Button variant="outline" onClick={() => navigate('/cervejas/nova')}>
                Seja o primeiro a adicionar!
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBeers.map((beer) => (
                <div 
                  key={beer.id} 
                  className="bg-white rounded-2xl shadow-soft overflow-hidden border border-gray-100 hover:shadow-large transition-all duration-300 cursor-pointer group transform hover:-translate-y-1"
                  onClick={() => handleBeerClick(beer)}
                >
                  {beer.image_url ? (
                    <div className="h-48 overflow-hidden relative">
                      <img 
                        src={beer.image_url} 
                        alt={beer.name} 
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" 
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                        <span className="text-white font-medium text-sm bg-white/20 backdrop-blur-md px-4 py-1 rounded-full">
                          Ver Detalhes
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="h-48 bg-gray-100 flex items-center justify-center text-gray-300 group-hover:bg-gray-200 transition-colors">
                      <BookOpen className="w-16 h-16 opacity-20" />
                    </div>
                  )}
                  
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-display font-bold text-xl text-dark-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {beer.name}
                      </h3>
                      <span className="px-3 py-1 bg-primary-50 text-primary-700 text-xs font-bold rounded-full uppercase flex-shrink-0 ml-2">
                        {beer.style}
                      </span>
                    </div>
                    
                    <div className="flex gap-4 text-xs font-medium text-dark-500 mb-4 bg-gray-50 p-2 rounded-lg">
                      <span className="flex-1 text-center border-r border-gray-200">
                        ABV: <span className="text-dark-900">{beer.abv_range || 'N/A'}</span>
                      </span>
                      <span className="flex-1 text-center">
                        IBU: <span className="text-dark-900">{beer.ibu_range || 'N/A'}</span>
                      </span>
                    </div>
                    
                    <p className="text-dark-600 text-sm line-clamp-3 mb-4">
                      {beer.description}
                    </p>
                    
                    {beer.pairing && (
                      <div className="flex items-start text-xs text-dark-500 pt-4 border-t border-gray-100">
                        <span className="mr-2 text-lg">🍽️</span>
                        <span className="line-clamp-1 italic">{beer.pairing}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
        title="" // Empty title to use custom header in body
        size="lg"
      >
        {selectedBeer && (
          <div className="space-y-6">
            {/* Custom Header inside Modal */}
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left: Image */}
              <div className="w-full md:w-1/3 flex-shrink-0">
                {selectedBeer.image_url ? (
                  <img 
                    src={selectedBeer.image_url} 
                    alt={selectedBeer.name} 
                    className="w-full h-64 md:h-full object-cover rounded-xl shadow-md" 
                  />
                ) : (
                  <div className="w-full h-64 bg-gray-100 rounded-xl flex items-center justify-center text-gray-300">
                    <BookOpen className="w-20 h-20 opacity-20" />
                  </div>
                )}
              </div>

              {/* Right: Info */}
              <div className="flex-1 space-y-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-3 py-1 bg-primary-100 text-primary-800 text-xs font-bold rounded-full uppercase">
                      {selectedBeer.style}
                    </span>
                    {selectedBeer.author && (
                      <div className="flex items-center text-xs text-gray-400" title={`Contribuído por ${selectedBeer.author.full_name}`}>
                        <User className="w-3 h-3 mr-1" />
                        {selectedBeer.author.full_name?.split(' ')[0]}
                      </div>
                    )}
                  </div>
                  <h2 className="font-display font-bold text-3xl text-dark-900 mb-4">
                    {selectedBeer.name}
                  </h2>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 text-center">
                      <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Teor Alcoólico</span>
                      <span className="font-bold text-dark-900 text-lg">{selectedBeer.abv_range || '-'}</span>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 text-center">
                      <span className="block text-xs text-gray-500 uppercase tracking-wider mb-1">Amargor (IBU)</span>
                      <span className="font-bold text-dark-900 text-lg">{selectedBeer.ibu_range || '-'}</span>
                    </div>
                  </div>
                </div>

                {selectedBeer.pairing && (
                  <div className="bg-amber-50 p-4 rounded-xl border border-amber-100">
                    <h4 className="font-bold text-amber-900 text-sm mb-2 flex items-center">
                      <span className="text-xl mr-2">🍽️</span> Harmonização
                    </h4>
                    <p className="text-amber-800 text-sm leading-relaxed">
                      {selectedBeer.pairing}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Full Description Area */}
            <div className="pt-6 border-t border-gray-100">
              <h3 className="font-bold text-dark-900 text-lg mb-3">Sobre o Estilo</h3>
              <div className="prose prose-amber max-w-none text-dark-600 text-sm leading-relaxed whitespace-pre-line">
                {selectedBeer.description}
              </div>
            </div>

            {selectedBeer.history && (
              <div className="pt-4">
                <h3 className="font-bold text-dark-900 text-lg mb-3">História & Curiosidades</h3>
                <div className="prose prose-amber max-w-none text-dark-600 text-sm leading-relaxed whitespace-pre-line">
                  {selectedBeer.history}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button variant="secondary" onClick={closeModal}>
                Fechar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default BeerEncyclopedia;